import type { DeviceStyle, NodeV2, NodeV2Style } from '@vedalib/editor'
import type { DeviceMode } from '@vedalib/editor'
import { isComplexElement, isNodeV2, isSlotElement, notEmpty } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'

import {
  getNodeWithFullChildren,
  getParentNode,
} from 'components/Editor/EditorContext/selectors/block'
import { genSubLayout } from 'components/Editor/NodeV2/constants'
import type { IGridArea } from 'components/Editor/NodeV2/formControls/Area/types'
import { safeParseArea, saveArea } from 'components/Editor/NodeV2/formControls/Area/utils'
import { safeParseCoords, saveCoords } from 'components/Editor/NodeV2/formControls/Coords/utils'
import type { FilterValueType } from 'components/Editor/NodeV2/formControls/Filter/types'
import { saveFilter } from 'components/Editor/NodeV2/formControls/Filter/utils'
import type { GridValueType } from 'components/Editor/NodeV2/formControls/Grid/types'
import { safeParseGrid, saveGrid } from 'components/Editor/NodeV2/formControls/Grid/utils'
import type { ISizeValue } from 'components/Editor/NodeV2/formControls/Size/types'
import { saveSize } from 'components/Editor/NodeV2/formControls/Size/utils'
import type { PxSizesValueType } from 'components/Editor/NodeV2/formControls/Sizes/types'
import { savePxSizes } from 'components/Editor/NodeV2/formControls/Sizes/utils'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import { mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, MapType } from 'services/Store/Project/types'
import { idMapper } from 'utils/editor/clone'

import {
  alignSelfLens,
  backdropFilterLens,
  blockElementsLens,
  borderRadiusLens,
  boxShadowLens,
  childrenLens,
  coordsLens,
  displayLens,
  doneLens,
  elementValueItemLens,
  elementValueItemsLens,
  elementValueLens,
  filterLens,
  flexAlignItemsLens,
  flexDirectionLens,
  flexFlowLens,
  flexGapLens,
  flexGrowLens,
  flexJustifyContentLens,
  flexShrinkLens,
  flexWrapLens,
  fullStyleLens,
  gridAreaLens,
  gridColumnsGapLens,
  gridColumnsLens,
  gridRowsGapLens,
  gridRowsLens,
  heightLens,
  hiddenLens,
  labelLens,
  maxHeightLens,
  maxWidthLens,
  minHeightLens,
  minWidthLens,
  nodesLens,
  opacityLens,
  paddingLens,
  positionLens,
  repeatableLens,
  styleLens,
  widthLens,
  zIndexLens,
} from '../lenses'

// NodeV2

export const updateNodeFullStyle = (nodeId: string, styles: DeviceStyle) => (block: Block) =>
  R.over(fullStyleLens(nodeId), R.mergeDeepLeft(styles))(block as Block)
export const updateNodeChildren = (nodeId: string, children: NodeV2[]) => (block: Block) => {
  const idMap = idMapper()
  const remapped = children.map((n) => remapNodeId(n, idMap))

  return R.compose(
    R.over(nodesLens, (nodes) => ({ ...nodes, ...R.indexBy(R.prop('id'), remapped) })),
    R.set(childrenLens(nodeId), remapped.map(R.prop('id'))),
  )(block as Block)
}

export const addGroupItem = (node: NodeV2, device: DeviceMode) => (block: Block) => {
  const style = mergeNodeStyle(node?.style || {}, device)
  const nodes = node.children.map((id) => block.schema.nodes[id]).filter(isNodeV2)

  const firstChild =
    style.display === 'flex'
      ? nodes.find((node) => mergeNodeStyle(node.style, device).flexFlow !== 'absolute')
      : nodes[0]
  return firstChild ? duplicateNode(firstChild.id, node.children.length)(block) : block
}

export const addNode =
  (
    destinationId: string,
    node: NodeV2,
    data?: { elements?: MapType<EditorElement>; nodes?: MapType<NodeV2> } | null,
    position: { index?: number | null } = {},
  ) =>
  (block: Block) => {
    const destination = block.schema.nodes[destinationId]
    if (!isNodeV2(destination) || !isNodeV2(node)) {
      return block
    }

    const element =
      isNodeV2(destination) && destination.elementId ? block?.elements[destination.elementId] : null

    if (isComplexElement(element)) {
      const index = position.index ?? element.value.items.length

      if (data) {
        return R.compose(
          R.over(nodesLens, (nodes) => ({ ...nodes, ...data.nodes, [node.id]: node })),
          R.over(blockElementsLens, (elements) => ({ ...elements, ...data.elements })),
          R.over(elementValueItemsLens(element.id), R.insert(index, { value: node.id })),
        )(block)
      }

      return R.compose(
        R.assocPath<NodeV2, Block>(['schema', 'nodes', node.id], node),
        R.over(elementValueItemsLens(element.id), R.insert(index, { value: node.id })),
      )(block)
    } else if (isSlotElement(element)) {
      const index = (position.index ?? element.value.item) ? 1 : 0

      if (data) {
        return R.compose(
          R.over(nodesLens, (nodes) => ({ ...nodes, ...data.nodes, [node.id]: node })),
          R.over(blockElementsLens, (elements) => ({ ...elements, ...data.elements })),
          R.set(elementValueItemLens(element.id), { value: node.id }),
        )(block)
      }

      return R.compose(
        R.assocPath<NodeV2, Block>(['schema', 'nodes', node.id], node),
        R.over(elementValueItemsLens(element.id), R.insert(index, { value: node.id })),
      )(block)
    } else {
      const index = position.index ?? destination.children.length

      if (data) {
        return R.compose(
          R.over(nodesLens, (nodes) => ({ ...nodes, ...data.nodes, [node.id]: node })),
          R.over(blockElementsLens, (elements) => ({ ...elements, ...data.elements })),
          R.over(childrenLens(destinationId), R.insert(index, node.id)),
        )(block)
      }

      return R.compose(
        R.assocPath<NodeV2, Block>(['schema', 'nodes', node.id], node),
        R.over(childrenLens(destinationId), R.insert(index, node.id)),
      )(block)
    }
  }

export const duplicateNode =
  (nodeId: string, idx?: number) =>
  (block: Block): Block => {
    const parent = getParentNode(nodeId, block)
    if (!parent) {
      return block
    }
    const index = idx ?? parent.children.indexOf(nodeId)
    const { root, elements, nodes } = cloneNode(block, nodeId)

    return R.compose(
      R.over(nodesLens, (n) => ({ ...n, ...nodes, [root.id]: root })),
      R.over(blockElementsLens, (e) => ({ ...e, ...elements })),
      R.over(childrenLens(parent.id), R.insert(index, root.id)),
    )(block as Block)
  }

export const moveNode =
  (
    sourceId: string | null,
    destinationId: string | null,
    targetId: string,
    index: number | null,
    data?: { elements?: MapType<EditorElement>; nodes?: MapType<NodeV2> } | null,
  ) =>
  (block: Block) => {
    const source = (sourceId && block.schema.nodes[sourceId]) as NodeV2 | null
    const destination = (destinationId && block.schema.nodes[destinationId]) as NodeV2 | null
    const target = (block.schema.nodes[targetId] ||
      data?.nodes?.[targetId] ||
      null) as NodeV2 | null
    const lastIndex = destination?.children.filter((id) => target?.id !== id).length ?? 0

    return R.compose<[Block], Block, Block, Block, Block>(
      // Transfer node
      source &&
        destination &&
        target &&
        source.id !== destination.id &&
        target.id !== destination.id
        ? transferNode(destination, target, index ?? lastIndex)
        : R.identity,
      // Reorder node
      source && destination && target && source.id === destination.id && typeof index === 'number'
        ? reorderNode(destination, targetId, index)
        : R.identity,
      // Add node
      !source && destination && target
        ? addNode(destination.id, target, data, { index: index ?? lastIndex })
        : R.identity,
      // Remove node
      source && !destination && target ? rejectNode(target.id) : R.identity,
    )(block as Block)
  }

export const cloneNode = (block: Block, nodeId: string) => {
  const parent = getParentNode(nodeId, block)
  if (!parent) {
    throw new Error('Parent node not found')
  }
  const mapId = idMapper()
  const [sourceRoot, ...sourceNodes] = getNodeWithFullChildren(block, nodeId).filter(isNodeV2)
  const sourceElements = [sourceRoot, ...sourceNodes]
    .map((n) => (n.elementId ? block.elements[n.elementId] : null))
    .filter(notEmpty)

  const root = remapNodeId(sourceRoot, mapId)
  const nodes = R.indexBy(
    R.prop('id'),
    sourceNodes.map((n) => remapNodeId(n, mapId)),
  )
  const elements = R.indexBy(
    R.prop('id'),
    sourceElements.map((e) => remapElementId(e, mapId)),
  )

  return { root, nodes: { ...nodes, [root.id]: root }, elements }
}

export const remapNodeId = (node: NodeV2, mapId = idMapper()): NodeV2 => ({
  ...node,
  ...(node.elementId && { elementId: mapId(node.elementId) }),
  id: mapId(node.id),
  children: node.children.map(mapId),
})

export const remapElementId = (element: EditorElement, mapId = idMapper()): EditorElement => ({
  ...element,
  ...(isComplexElement(element) && {
    value: {
      ...element.value,
      items: element.value.items.map((item) => ({ ...item, value: mapId(item.value) })),
    },
  }),
  ...(isSlotElement(element) && {
    value: {
      ...element.value,
      item: element.value.item ? { value: mapId(element.value.item.value) } : undefined,
    },
  }),
  id: mapId(element.id),
})

export const transferNode = (destination: NodeV2, target: NodeV2, index: number) =>
  R.compose(insertNode(destination.id, target.id, index), rejectNode(target.id))

export const reorderNode =
  (destination: NodeV2, targetId: string, index: number) => (block: Block) => {
    if (
      isNodeV2(destination) &&
      destination.elementId &&
      isComplexElement(block.elements[destination.elementId])
    ) {
      return R.over(elementValueItemsLens(destination.elementId), (children) =>
        R.compose(
          R.filter<{ value: string } | null, { value: string }>(notEmpty),
          R.insertAll<{ value: string } | null>(index, [
            { ...children.find(({ value }) => value === targetId), value: targetId },
          ]),
          R.map<{ value: string }, { value: string } | null>((item) =>
            targetId === item.value ? null : item,
          ),
        )(children),
      )(block)
    }
    // if (
    //   isNodeV2(destination) &&
    //   destination.elementId &&
    //   isSlotElement(block.elements[destination.elementId])
    // ) {
    //   return R.over(elementValueItemsLens(destination.elementId), (children) =>
    //     R.compose(
    //       R.filter<{ value: string } | null, { value: string }>(notEmpty),
    //       R.insertAll<{ value: string } | null>(index, [
    //         { ...children.find(({ value }) => value === targetId), value: targetId },
    //       ]),
    //       R.map<{ value: string }, { value: string } | null>((item) =>
    //         targetId === item.value ? null : item,
    //       ),
    //     )(children),
    //   )(block)
    // }
    return R.over(childrenLens(destination.id), (children) =>
      R.compose(
        R.filter<string | null, string>(notEmpty),
        R.insertAll<string | null>(index, [targetId]),
        R.map<string, string | null>((item) => (targetId === item ? null : item)),
      )(children),
    )(block)
  }

export const insertNode =
  (destinationId: string, targetId: string, index: number) => (block: Block) => {
    const destination = block.schema.nodes[destinationId]
    const target = block.schema.nodes[targetId]

    if (
      isNodeV2(destination) &&
      isNodeV2(target) &&
      destination.elementId &&
      isComplexElement(block.elements[destination.elementId])
    ) {
      const value = R.view(elementValueLens(destination.elementId), block)
      const newItem = target.elementId ? genSubLayout(generateId(), [target.id]) : target
      const items = R.insert(index, { value: newItem.id })(value.items)
      return R.compose(
        R.over(nodesLens, (nodes) => ({ ...nodes, [newItem.id]: newItem })),
        R.over(elementValueLens(destination.elementId), (value) => ({ ...value, items })),
      )(block as Block)
    }
    if (
      isNodeV2(destination) &&
      isNodeV2(target) &&
      destination.elementId &&
      isSlotElement(block.elements[destination.elementId])
    ) {
      const newItem = target.elementId ? genSubLayout(generateId(), [target.id]) : target
      const item = { value: newItem.id }
      return R.compose(
        R.over(nodesLens, (nodes) => ({ ...nodes, [newItem.id]: newItem })),
        R.over(elementValueLens(destination.elementId), (value) => ({ ...value, item })),
      )(block as Block)
    }

    return R.over(childrenLens(destinationId), R.insert(index, targetId))(block)
  }

export const omitNode = (targetId: string) => (block: Block) => {
  return R.over(nodesLens, R.omit([targetId]))(block as Block)
}

export const omitNodeWithChildren = (targetId: string) => (block: Block) => {
  const nodes = getNodeWithFullChildren(block, targetId)
  const elementIds = nodes.map((node) => (isNodeV2(node) ? node.elementId : null)).filter(notEmpty)
  return R.compose(
    R.over(nodesLens, R.omit(nodes.map((item) => item.id))),
    R.over(blockElementsLens, R.omit(elementIds)),
    rejectNode(targetId),
  )(block as Block)
}

export const rejectNode = (targetId: string) => (block: Block) => {
  const source = getParentNode(targetId, block)
  const element = source?.elementId ? block.elements[source.elementId] : null
  if (source && element && isComplexElement(element)) {
    const items = element.value.items.filter((item: { value: string }) => item.value !== targetId)
    const last = items.length === 0
    const parent = getParentNode(source.id, block)
    return R.compose(
      last ? R.over(blockElementsLens, R.omit([element.id])) : R.identity<Block>,
      last ? R.over(nodesLens, R.omit([source.id])) : R.identity<Block>,
      last ? R.over(childrenLens(parent?.id), R.reject(R.equals(source.id))) : R.identity<Block>,
      R.over(elementValueLens(source.elementId), (value) => ({ ...value, items })),
    )(block as Block)
  }
  if (source && element && isSlotElement(element)) {
    const parent = getParentNode(source.id, block)
    return R.compose(
      R.over(blockElementsLens, R.omit([element.id])),
      R.over(nodesLens, R.omit([source.id])),
      R.over(childrenLens(parent?.id), R.reject(R.equals(source.id))),
      R.over(elementValueLens(source.elementId), ({ item, ...value }) => value),
    )(block as Block)
  }
  return R.over(childrenLens(source?.id), R.reject(R.equals(targetId)))(block as Block)
}

// NodeV2 Common
export const setLabel = (nodeId: string, value: string | null) => R.set(labelLens(nodeId), value)

export const toggleHidden = (node: NodeV2, device: DeviceMode, hidden: boolean) => (block: Block) =>
  R.set(hiddenLens(node.id, device), hidden)(block as Block)

export const toggleDone = (node: NodeV2) => (block: Block) =>
  R.over(doneLens(node.id), (hidden) => !hidden)(block as Block)

export const setRepeatable = (nodeId: string, repeat?: boolean) => (block: Block) =>
  R.set(repeatableLens(nodeId), repeat ?? false)(block as Block)

export const resetStyles = (nodeId: string, device: DeviceMode) =>
  R.set(styleLens(nodeId, device), {})

export const resetStyle =
  (nodeId: string, device: DeviceMode, styleKey: keyof NodeV2Style) => (block: Block) =>
    R.over(styleLens(nodeId, device), (style) => R.omit([styleKey], style))(block as Block)

// NodeV2Styles Geometry
export const setDisplay =
  (nodeId: string, device: DeviceMode, display?: NodeV2Style['display']) => (block: Block) =>
    R.set(displayLens(nodeId, device), display)(block as Block)

export const setPosition =
  (node: NodeV2, device: DeviceMode, position: NodeV2Style['position']) => (block: Block) =>
    R.set(positionLens(node.id, device), position)(block as Block)

export const setWidth =
  (nodeId: string, device: DeviceMode, value?: ISizeValue | number | null) => (block: Block) => {
    const width =
      typeof value === 'number' ? { value: Math.round(value), unit: 'px' as const } : value
    return R.set(widthLens(nodeId, device), saveSize(width ?? null))(block as Block)
  }

export const setHeight =
  (nodeId: string, device: DeviceMode, value?: ISizeValue | number | null) => (block: Block) => {
    const height =
      typeof value === 'number' ? { value: Math.round(value), unit: 'px' as const } : value
    return R.set(heightLens(nodeId, device), saveSize(height ?? null))(block as Block)
  }

export const setMinWidth =
  (nodeId: string, device: DeviceMode, minHeight: ISizeValue) => (block: Block) =>
    R.set(minWidthLens(nodeId, device), saveSize(minHeight))(block as Block)
export const setMaxWidth =
  (nodeId: string, device: DeviceMode, minHeight: ISizeValue) => (block: Block) =>
    R.set(maxWidthLens(nodeId, device), saveSize(minHeight))(block as Block)

export const setMinHeight =
  (nodeId: string, device: DeviceMode, minHeight: ISizeValue) => (block: Block) =>
    R.set(minHeightLens(nodeId, device), saveSize(minHeight))(block as Block)
export const setMaxHeight =
  (nodeId: string, device: DeviceMode, minHeight: ISizeValue) => (block: Block) =>
    R.set(maxHeightLens(nodeId, device), saveSize(minHeight))(block as Block)

export const setPadding =
  (nodeId: string, device: DeviceMode, padding?: PxSizesValueType | null) => (block: Block) =>
    R.set(paddingLens(nodeId, device), savePxSizes(padding))(block as Block)

export const setBorderRadius =
  (nodeId: string, device: DeviceMode, radius?: PxSizesValueType | null) => (block: Block) =>
    R.set(borderRadiusLens(nodeId, device), savePxSizes(radius))(block as Block)

// NodeV2Styles Grid
export const setGridArea =
  (nodeId: string, device: DeviceMode, gridArea?: IGridArea | null) => (block: Block) =>
    gridArea ? R.set(gridAreaLens(nodeId, device), saveArea(gridArea))(block as Block) : block

export const decreaseGridArea =
  (node: NodeV2, device: DeviceMode, direction: 'row' | 'column') => (block: Block) => {
    const { gridArea } = mergeNodeStyle(R.view(fullStyleLens(node.id), block as Block), device)
    const currentArea = safeParseArea(gridArea)
    const newArea = saveArea({
      ...currentArea,
      ...(direction === 'row' && { rowEnd: Math.max(2, currentArea.rowEnd - 1) }),
      ...(direction === 'column' && { colEnd: Math.max(2, currentArea.colEnd - 1) }),
    })
    return R.set(gridAreaLens(node.id, device), newArea)(block as Block)
  }
export const increaseGridArea =
  (node: NodeV2, device: DeviceMode, direction: 'row' | 'column') => (block: Block) => {
    const { gridArea } = mergeNodeStyle(R.view(fullStyleLens(node.id), block as Block), device)
    const currentArea = safeParseArea(gridArea)
    const newArea = saveArea({
      ...currentArea,
      ...(direction === 'row' && { rowEnd: Math.max(2, currentArea.rowEnd + 1) }),
      ...(direction === 'column' && { colEnd: Math.max(2, currentArea.colEnd + 1) }),
    })
    return R.set(gridAreaLens(node.id, device), newArea)(block as Block)
  }

export const moveGridArea =
  (nodeId: string, device: DeviceMode, direction: ('up' | 'down' | 'left' | 'right')[]) =>
  (block: Block) => {
    const parent = getParentNode(nodeId, block)
    const node = block.schema.nodes[nodeId] as NodeV2
    const { gridRows, gridColumns } = mergeNodeStyle(parent?.style || EMPTY_OBJECT, device)
    const parentRows = safeParseGrid(gridRows)
    const lastRow = parentRows.items[parentRows.items.length - 1]?.data
    const parentCols = safeParseGrid(gridColumns)
    const lastCol = parentCols.items[parentCols.items.length - 1]?.data
    const { gridArea } = mergeNodeStyle(node?.style, device)
    const { rowStart, rowEnd, colStart, colEnd } = safeParseArea(gridArea)
    const up = direction.filter((item) => item === 'up').length
    const down = direction.filter((item) => item === 'down').length
    const left = direction.filter((item) => item === 'left').length
    const right = direction.filter((item) => item === 'right').length
    const newArea = {
      rowStart: rowStart - up + down,
      colStart: colStart - left + right,
      rowEnd: rowEnd - up + down,
      colEnd: colEnd - left + right,
    }

    return R.compose<[Block], Block, Block, Block>(
      (up || down) && parent && parentRows.items.length + 1 < newArea.rowEnd
        ? addGridRows(parent?.id, device, 1, undefined, lastRow)
        : R.identity,
      (left || right) && parent && parentCols.items.length + 1 < newArea.colEnd
        ? addGridCols(parent?.id, device, 1, undefined, lastCol)
        : R.identity,
      R.set(gridAreaLens(nodeId, device), saveArea(newArea)),
    )(block as Block)
  }

export const setGridCol =
  (nodeId: string, device: DeviceMode, value: GridValueType) => (block: Block) =>
    R.set(gridColumnsLens(nodeId, device), saveGrid(value))(block as Block)

export const setColumns =
  (nodeId: string, device: DeviceMode, value: GridValueType) => (block: Block) =>
    R.set(gridColumnsLens(nodeId, device), saveGrid(value))(block as Block)

export const setColumnsGap =
  (nodeId: string, device: DeviceMode, value: number) => (block: Block) =>
    R.set(gridColumnsGapLens(nodeId, device), value)(block as Block)

export const resetColumns = (nodeId: string, device: DeviceMode) =>
  R.over(styleLens(nodeId, device), (styles) => R.omit(['gridColumns', 'gridColumnsGap'], styles))

export const setGridRow =
  (nodeId: string, device: DeviceMode, value: GridValueType) => (block: Block) =>
    R.set(gridRowsLens(nodeId, device), saveGrid(value))(block as Block)

export const setRows =
  (nodeId: string, device: DeviceMode, value: GridValueType) => (block: Block) =>
    R.set(gridRowsLens(nodeId, device), saveGrid(value))(block as Block)

export const setRowsGap = (nodeId: string, device: DeviceMode, value: number) => (block: Block) =>
  R.set(gridRowsGapLens(nodeId, device), value)(block as Block)

export const resetRows = (nodeId: string, device: DeviceMode) =>
  R.over(styleLens(nodeId, device), (styles) =>
    R.omit(['gridRows', 'gridRowsGap', 'gridAutoRows'], styles),
  )

export const removeGridRows =
  (nodeId: string, device: DeviceMode, count = 1, index?: number) =>
  (block: Block) => {
    const { gridRows } = mergeNodeStyle(R.view(fullStyleLens(nodeId), block as Block), device)
    const parsed = safeParseGrid(gridRows)
    const newRows = {
      ...parsed,
      items:
        typeof index === 'number'
          ? parsed.items.filter((_item, i) => i !== index)
          : parsed.items.slice(0, -count),
    }
    return R.set(gridRowsLens(nodeId, device), saveGrid(newRows))(block as Block)
  }

export const addGridRows =
  (nodeId: string, device: DeviceMode, count = 1, index?: number, row: ISizeValue | null = null) =>
  (block: Block) => {
    const { gridRows } = mergeNodeStyle(R.view(fullStyleLens(nodeId), block as Block), device)
    const parsed = safeParseGrid(gridRows)
    const newItems = Array(count)
      .fill({ data: row })
      .map((item) => ({ ...item, value: generateId() }))
    const newRows = {
      ...parsed,
      items: R.insertAll(index ?? parsed.items.length, newItems, parsed.items),
    }

    return R.set(gridRowsLens(nodeId, device), saveGrid(newRows))(block as Block)
  }

export const addGridCols =
  (nodeId: string, device: DeviceMode, count = 1, index?: number, col: ISizeValue | null = null) =>
  (block: Block) => {
    const { gridColumns } = mergeNodeStyle(R.view(fullStyleLens(nodeId), block as Block), device)
    const parsed = safeParseGrid(gridColumns)
    const newItems = Array(count)
      .fill({ data: col })
      .map((item) => ({ ...item, value: generateId() }))
    const newCols = {
      ...parsed,
      items: R.insertAll(index ?? parsed.items.length, newItems, parsed.items),
    }
    return R.set(gridColumnsLens(nodeId, device), saveGrid(newCols))(block as Block)
  }

export const removeGridColumns =
  (nodeId: string, device: DeviceMode, count = 1, index?: number) =>
  (block: Block) => {
    const { gridColumns } = mergeNodeStyle(R.view(fullStyleLens(nodeId), block as Block), device)
    const parsed = safeParseGrid(gridColumns)
    const newColumns = {
      ...parsed,
      items:
        typeof index === 'number'
          ? parsed.items.filter((_item, i) => i !== index)
          : parsed.items.slice(0, -count),
    }
    return R.set(gridColumnsLens(nodeId, device), saveGrid(newColumns))(block as Block)
  }

export const setZIndex = (nodeId: string, device: DeviceMode, zIndex?: number) => (block: Block) =>
  R.set(zIndexLens(nodeId, device), zIndex)(block as Block)

export const decrementZIndex = (nodeId: string, device: DeviceMode) => (block: Block) => {
  const newZIndexes = recalculateZIndex(block, nodeId, device, R.dec)
  const cbs = newZIndexes?.map((item) => R.set(zIndexLens(item.id, device), item.zIndex))

  return R.compose(...(cbs as [(obj: Block) => Block]))(block as Block)
}

export const incrementZIndex = (nodeId: string, device: DeviceMode) => (block: Block) => {
  const newZIndex = recalculateZIndex(block, nodeId, device, R.inc)
  const cbs = newZIndex?.map((item) => R.set(zIndexLens(item.id, device), item.zIndex))

  return R.compose(...(cbs as [(obj: Block) => Block]))(block as Block)
}

const recalculateZIndex = (
  block: Block,
  nodeId: string,
  device: DeviceMode,
  cb: (n: number) => number,
) => {
  const children = getParentNode(nodeId, block)?.children || []
  const zIndexes = children
    .map((id) => block.schema.nodes[id])
    .filter(isNodeV2)
    .map(({ id, style }) => {
      const zIndex = Math.abs(mergeNodeStyle(style, device).zIndex || 1)
      return { id, zIndex: id === nodeId ? cb(zIndex) : zIndex }
    })
    .sort((a, b) => a.zIndex - b.zIndex)

  const uniqSet = [...new Set(zIndexes?.map((i) => i.zIndex))]
  const map = new Map(uniqSet.map((value, index) => [value, index + 1]))
  return zIndexes.map((i) => ({ ...i, zIndex: map.get(i.zIndex) || 1 }))
}

// NodeV2Styles Flex
export const setFlexGap = (nodeId: string, device: DeviceMode, gap?: number) => (block: Block) =>
  R.set(flexGapLens(nodeId, device), gap ?? 0)(block as Block)

export const setFlexAlignItems =
  (nodeId: string, device: DeviceMode, align?: NodeV2Style['flexAlignItems']) => (block: Block) =>
    R.set(flexAlignItemsLens(nodeId, device), align)(block as Block)

export const setFlexJustifyContent =
  (nodeId: string, device: DeviceMode, justify?: NodeV2Style['flexJustifyContent']) =>
  (block: Block) =>
    R.set(flexJustifyContentLens(nodeId, device), justify)(block as Block)

export const setFlexWrap =
  (nodeId: string, device: DeviceMode, wrap?: NodeV2Style['flexWrap']) => (block: Block) =>
    R.set(flexWrapLens(nodeId, device), wrap)(block as Block)

export const setFlexDirection =
  (nodeId: string, device: DeviceMode, direction?: NodeV2Style['flexDirection']) =>
  (block: Block) =>
    R.set(flexDirectionLens(nodeId, device), direction)(block as Block)

export const setFlexFlow =
  (node: NodeV2, device: DeviceMode, position: NodeV2Style['flexFlow']) => (block: Block) =>
    R.set(flexFlowLens(node.id, device), position)(block as Block)

export const setFlexShrink =
  (nodeId: string, device: DeviceMode, shrink?: number) => (block: Block) =>
    R.set(flexShrinkLens(nodeId, device), shrink ?? 0)(block as Block)

export const setFlexGrow = (nodeId: string, device: DeviceMode, grow?: number) => (block: Block) =>
  R.set(flexGrowLens(nodeId, device), grow ?? 0)(block as Block)

export const setAlignSelf =
  (nodeId: string, device: DeviceMode, align?: NodeV2Style['alignSelf']) => (block: Block) =>
    R.set(alignSelfLens(nodeId, device), align)(block as Block)

/// NodeV2Styles Absolute
// export const setAbsoluteType =
//   (nodeId: string, device: DeviceMode, type?: NodeV2Style['absoluteType']) => (block: Block) =>
//     R.set(absoluteTypeLens(nodeId, device), type)(block as Block)

export const updateCoordinates =
  (
    nodeId: string,
    device: DeviceMode,
    { top, left, right, bottom }: { top?: number; left?: number; right?: number; bottom?: number },
  ) =>
  (block: Block) =>
    R.over(coordsLens(nodeId, device), (coords) => {
      const oldCoords = safeParseCoords(coords)

      return saveCoords({
        ...oldCoords,
        ...(typeof top === 'number' && { top: Math.floor(top), bottom: undefined }),
        ...(typeof bottom === 'number' && { bottom: Math.floor(bottom), top: undefined }),
        ...(typeof left === 'number' && { left: Math.floor(left), right: undefined }),
        ...(typeof right === 'number' && { right: Math.floor(right), left: undefined }),
      })
    })(block as Block)

export const moveCoordinates =
  (
    nodeId: string,
    device: DeviceMode,
    direction: ('up' | 'down' | 'left' | 'right')[],
    multiplier = 1,
  ) =>
  (block: Block) => {
    const node = block.schema.nodes[nodeId] as NodeV2
    const nodeStyle = mergeNodeStyle(node?.style, device)

    const up = direction.filter((item) => item === 'up').length * multiplier
    const down = direction.filter((item) => item === 'down').length * multiplier
    const left = direction.filter((item) => item === 'left').length * multiplier
    const right = direction.filter((item) => item === 'right').length * multiplier
    const coords = safeParseCoords(nodeStyle.coords)
    const baseTop = coords.top
    const baseLeft = coords.left
    const baseRight = coords.right
    const baseBottom = coords.bottom
    const newTop = (baseTop || 0) + down - up
    const newLeft = (baseLeft || 0) + right - left
    const newRight = (baseRight || 0) - right + left
    const newBottom = (baseBottom || 0) - down + up

    return updateCoordinates(nodeId, device, {
      left: typeof baseLeft === 'number' ? newLeft : undefined,
      top: typeof baseTop === 'number' ? newTop : undefined,
      right: typeof baseRight === 'number' ? newRight : undefined,
      bottom: typeof baseBottom === 'number' ? newBottom : undefined,
    })(block)
  }

// NodeV2Styles Common
export const setOpacity =
  (nodeId: string, device: DeviceMode, opacity?: number | null) => (block: Block) =>
    R.set(opacityLens(nodeId, device), opacity ?? 0)(block as Block)

export const setBoxShadow =
  (nodeId: string, device: DeviceMode, boxShadow?: string | null) => (block: Block) =>
    R.set(boxShadowLens(nodeId, device), boxShadow)(block as Block)

export const setBackdropFilter =
  (nodeId: string, device: DeviceMode, backdropFilter?: FilterValueType | null) => (block: Block) =>
    R.set(backdropFilterLens(nodeId, device), saveFilter(backdropFilter))(block as Block)

export const setFilter =
  (nodeId: string, device: DeviceMode, filter?: FilterValueType | null) => (block: Block) =>
    R.set(filterLens(nodeId, device), saveFilter(filter))(block as Block)
